<template>
  <section
    class="approach section-card approach--mobile"
    aria-labelledby="approach-mobile-heading-1 approach-mobile-heading-2"
  >
    <div v-reveal>
      <p class="label">Our Approach</p>
      <h2 id="approach-mobile-heading-1" class="title title-lg approach-title-first">
        Built for<br />shipping companies
      </h2>
    </div>

    <div v-reveal>
      <div class="panel-image">
        <img
          :src="warehouseImage"
          alt="Warehouse owner reviewing inventory with a tablet"
          class="approach-image approach-image--zoom"
          loading="lazy"
        />
      </div>

      <div class="approach-reasons">
        <div v-for="reason in shippingReasons" :key="reason.icon" class="panel-reason">
          <div class="reason-heading">
            <component :is="reason.icon" class="reason-icon" aria-hidden="true" />
            <h3 class="title title-md">
              <template v-if="Array.isArray(reason.title)">{{ reason.title[0] }}<br />
                {{ reason.title[1] }}</template>
              <template v-else>{{ reason.title }}</template>
            </h3>
          </div>
          <p class="subtitle subtitle-reason">{{ reason.description }}</p>
        </div>
      </div>
    </div>

    <h2 id="approach-mobile-heading-2" class="title title-lg approach-title-second">
      Here for their customers
    </h2>

    <div v-reveal>
      <div class="panel-image">
        <img
          :src="familyPackingImage"
          alt="Two people packing a shipment together"
          class="approach-image"
          loading="lazy"
        />
      </div>

      <div class="approach-reasons">
        <div v-for="reason in customerReasons" :key="reason.icon" class="panel-reason">
          <div class="reason-heading">
            <component :is="reason.icon" class="reason-icon" aria-hidden="true" />
            <h3 class="title title-md">
              <template v-if="Array.isArray(reason.title)">{{ reason.title[0] }}<br />
                {{ reason.title[1] }}</template>
              <template v-else>{{ reason.title }}</template>
            </h3>
          </div>
          <p class="subtitle subtitle-reason">{{ reason.description }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import warehouseImage from '../assets/Owner_Warehouse.jpg';
import familyPackingImage from '../assets/Family_Packing.jpg';
import reveal from '../directives/scrollReveal';
import '../assets/styles/landing-shared.scss';

export default {
  name: 'Landing2ApproachMobile',
  directives: {
    reveal,
  },
  data() {
    return {
      warehouseImage,
      familyPackingImage,
      shippingReasons: [
        {
          icon: 'b-icon-currency-dollar',
          title: ['Get Paid', 'Faster'],
          description: 'Funds land automatically once a delivery is confirmed.',
        },
        {
          icon: 'b-icon-people',
          title: 'Attract More Customers',
          description: 'Stay visible so new customers come to you.',
        },
        {
          icon: 'b-icon-graph-up',
          title: 'Operate Efficiently',
          description: 'Fewer scheduling conflicts, more accurate planning.',
        },
      ],
      customerReasons: [
        {
          icon: 'b-icon-phone',
          title: 'Easy Online Booking',
          description: 'Book from anywhere, anytime, from your device.',
        },
        {
          icon: 'b-icon-search',
          title: 'Discover Your Options',
          description: 'Compare price, speed, and carrier rating.',
        },
        {
          icon: 'b-icon-geo-alt',
          title: 'Track Everything',
          description: 'Know the moment your package arrives.',
        },
      ],
    };
  },
};
</script>

<style lang="scss" scoped>
@import '../assets/styles/fluid';

.approach--mobile {
  display: none;
}

.approach-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* the desktop version wraps these in a text-align:center .approach-header;
   flattened here, so centering is applied directly to each element */
.label,
.title-lg {
  text-align: center;
}

.reason-heading {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
}

.reason-icon {
  flex: 0 0 auto;
  width: 1rem;
  height: 1rem;
  color: var(--color-primary);
}

.subtitle-reason {
  margin-top: 0.75rem;
  font-size: 0.75rem;
  line-height: 1.125rem;
  color: var(--color-text-muted);
}

.panel-image {
  width: 100%;
  height: 12rem;
  margin-bottom: 1rem;
  background: #D9D9D9;
  border-radius: 1.5rem;
  overflow: hidden;
}

/* first two reason cards sit side by side, the third spans full width below */
.approach-reasons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.panel-reason {
  display: flex;
  flex-direction: column;
  padding: 1.25rem;
  background: var(--color-card-bg);
  border-radius: 1.09375rem;
}

.panel-reason:last-child {
  grid-column: 1 / -1;
}

@media (max-width: 767px) {
  .approach {
    padding: 4rem 1.375rem;
  }

  .approach--mobile {
    display: flex;
    flex-direction: column;
  }

  /* .approach--mobile prefix matters here: the shared .title rule (in
     landing-shared.scss) sets margin:0 at .Landing2 .title specificity,
     which would tie with a bare .approach-title-first selector and come
     down to unpredictable load order. Two classes reliably wins instead. */
  .approach--mobile .approach-title-first {
    margin-bottom: 5rem;
    font-size: 1.875rem;
    line-height: 1.875rem;
  }

  .approach--mobile .approach-title-second {
    margin-top: 4rem;
    margin-bottom: 3.5625rem;
  }

  .approach--mobile .title-md {
    font-size: 1rem;
    line-height: 1.1875rem;
  }
}

@media (min-width: 768px) and (max-width: 1439px) {
  .approach {
    padding: fluid(4rem, 6rem) fluid(1.375rem, 3.75rem);
  }

  .approach--mobile {
    display: flex;
    flex-direction: column;
  }

  .approach--mobile .approach-title-first {
    margin-bottom: 5rem;
  }

  .approach--mobile .approach-title-second {
    margin-top: 4rem;
    margin-bottom: 3.5625rem;
  }

  .approach--mobile .title-md {
    font-size: fluid(1rem, 1.5rem);
    line-height: fluid(1.1875rem, 2rem);
  }

  .reason-heading {
    gap: fluid(0.5rem, 1rem);
  }

  .reason-icon {
    width: fluid(1rem, 2rem);
    height: fluid(1rem, 2rem);
  }

  .subtitle-reason {
    font-size: fluid(0.75rem, 1rem);
    line-height: fluid(1.125rem, 1.5rem);
  }

  .panel-image {
    height: fluid(12rem, 20rem);
  }

  .approach-reasons {
    gap: fluid(0.75rem, 1.5rem);
  }

  .panel-reason {
    padding: fluid(1.25rem, 2rem) fluid(1.25rem, 1.5rem);
  }
}
</style>
